// Step 6: about you + consent. data-field names match the server's dotted paths
// (contributor.email, visibilityChoice, consent.accepted...) so a server error lands on its field.
// Visibility has NO default (hard rule 3); credit defaults to consent.credit.default.
// What people type stays in memory only: it is never written to IndexedDB or the log.
import { isBackendConfigured } from '../lib/config.js';
import { logger } from '../lib/log.js';
import { showErrors, clearErrors, formValues, EMAIL_RE, looksLikePhone } from '../lib/forms.js';
import { $, $$, show } from './dom.js';
import { checkImageFile, fileSignature } from './files.js';

const log = logger('consent');

// Same rule as the server: 7 to 15 digits, and only the characters phone numbers use.
function phoneOk(s) {
  const digits = s.replace(/\D/g, '');
  return looksLikePhone(s) && digits.length <= 15 && /^[0-9+().\-\s/xXextEXT#]+$/.test(s);
}

export function createAboutScreen(app) {
  const { root, state, cfg } = app;
  const sec = $('[data-step="about"]', root);
  const form = $('[data-about-form]', sec);
  const otherWrap = $('[data-connection-other]', form);
  const submitBtn = $('[data-about-submit]', form);
  const photoInput = $('#rec-photo', form);
  const preview = $('[data-photo-preview]', form);
  const previewImg = $('[data-photo-img]', form);
  const previewName = $('[data-photo-name]', form);
  const consentBox = $('#rec-consent', form);
  const connIds = new Set((cfg.connections || []).map((c) => c.id));
  let previewUrl = null;

  function syncOther() {
    const v = formValues(form).connection;
    show(otherWrap, v === 'other');
  }
  form.addEventListener('change', (e) => {
    if (e.target.name === 'connection') syncOther();
    if (e.target === consentBox) {
      state.consentAt = consentBox.checked ? new Date().toISOString() : null;
      log.info('consent_checkbox', { checked: consentBox.checked });
    }
    if (e.target.name === 'visibilityChoice') log.info('visibility_choice', { visibility: e.target.value });
  });

  function clearPhoto() {
    state.photo = null;
    photoInput.value = '';
    if (previewUrl) { URL.revokeObjectURL(previewUrl); previewUrl = null; }
    previewImg.removeAttribute('src');
    show(preview, false);
  }
  photoInput.addEventListener('change', () => {
    const file = photoInput.files && photoInput.files[0];
    if (!file) { clearPhoto(); return; }
    const r = checkImageFile(file);
    if (!r.ok) {
      log.warn('photo_rejected', { fileType: file.type || '(none)', bytes: file.size });
      clearPhoto();
      showErrors(form, [{ field: 'photo', message: r.message }], { heading: 'We could not use that photo' });
      return;
    }
    const errBox = $('[data-field="photo"] .field__error', form);
    if (errBox) clearErrors(form);
    state.photo = { file, type: r.type, ext: r.ext, sig: fileSignature(file) };
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    previewUrl = URL.createObjectURL(file);
    previewImg.hidden = false;
    previewImg.onerror = () => { previewImg.hidden = true; }; // HEIC cannot preview in most browsers
    previewImg.src = previewUrl;
    previewImg.alt = 'The photo you chose';
    previewName.textContent = `Photo added (${Math.max(1, Math.round(file.size / 1024))} KB).`;
    show(preview, true);
    log.info('photo_selected', { fileType: r.type, bytes: file.size });
  });
  app.on('photo-remove', () => { clearPhoto(); photoInput.focus(); log.info('photo_removed', {}); });

  function validate(v) {
    const errors = [];
    if (!v.name) errors.push({ field: 'contributor.name', message: 'Please tell us your name.' });
    if (v.email && !EMAIL_RE.test(v.email)) errors.push({ field: 'contributor.email', message: 'That email address does not look right. Check it, or leave it empty.' });
    if (v.phone && !phoneOk(v.phone)) errors.push({ field: 'contributor.phone', message: 'That phone number does not look right. Check it, or leave it empty.' });
    if (!v.connection || !connIds.has(v.connection)) errors.push({ field: 'contributor.connection', message: 'Choose how you are connected to St. Theresa.' });
    else if (v.connection === 'other' && !v.connectionOther) errors.push({ field: 'contributor.connectionOther', message: 'Tell us your connection in a few words.' });
    if (!v.yearsInvolved) errors.push({ field: 'contributor.yearsInvolved', message: 'Tell us roughly which years you were part of St. Theresa. For example: 1975 to today.' });
    if (!v.visibilityChoice) errors.push({ field: 'visibilityChoice', message: 'Choose who may hear your recording.' });
    if (!v.credit) errors.push({ field: 'contributor.credit', message: 'Choose how we should name you.' });
    if (!v.consentAccepted) errors.push({ field: 'consent.accepted', message: `Check the box to agree before you ${isBackendConfigured() ? 'send' : 'continue'}.` });
    return errors;
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const v = formValues(form);
    const errors = validate(v);
    if (errors.length) {
      log.warn('consent_form_invalid', { fields: errors.map((x) => x.field) });
      showErrors(form, errors);
      return;
    }
    clearErrors(form);
    state.form = {
      name: v.name, email: v.email, phone: v.phone, connection: v.connection,
      connectionOther: v.connection === 'other' ? v.connectionOther : null,
      yearsInvolved: v.yearsInvolved, visibilityChoice: v.visibilityChoice, credit: v.credit,
    };
    if (!state.consentAt) state.consentAt = new Date().toISOString();
    log.info('consent_form_ok', {
      visibility: v.visibilityChoice, credit: v.credit, connection: v.connection,
      hasEmail: !!v.email, hasPhone: !!v.phone, hasPhoto: !!state.photo,
    });
    app.go('send', { auto: true });
  });

  /** Server validation errors come back here: show them on the field they name. */
  function showServerError(field, message) {
    if (!form.querySelector(`[data-field="${CSS.escape(field)}"]`)) return false;
    app.go('about', { focus: false });
    showErrors(form, [{ field, message }]);
    return true;
  }

  function reset() {
    form.reset(); // restores the default credit choice and clears visibility (no default)
    clearErrors(form);
    clearPhoto();
    syncOther();
  }

  return {
    showServerError,
    reset,
    onShow() {
      submitBtn.textContent = isBackendConfigured() ? 'Send my recording' : 'Continue';
      // Prefill the connection from the role they picked, unless they already chose one here.
      const checked = $$('input[name="connection"]', form).some((r) => r.checked);
      if (!checked && connIds.has(state.draft.role)) {
        const r = $(`input[name="connection"][value="${state.draft.role}"]`, form);
        if (r) r.checked = true;
      }
      syncOther();
    },
  };
}
